import React, { useEffect, useRef } from 'react'
import { PrinterIcon, DocumentTextIcon } from '@heroicons/react/24/outline'
import { useSelector } from '../../../Flux/index.js'
import type { ReportBlock } from '../../../Generated/ReportBlock.ts'

/**
 * The section outline: a clean table-of-contents of the report's HEADINGS only. We deliberately do NOT list
 * every block — the non-heading types (conditional/prose/table/chart/provenance) are implementation detail,
 * and a prose block's `text` is the RAW template (`{{diversityMetrics#sampleCount}}`), which must never be
 * shown to the user. Headings carry the reader-facing section names.
 */
function SectionOutline({ blocks }: { blocks: ReportBlock[] }) {
  const sections = blocks
    .filter((b) => b.type === 'heading')
    .map((b) => (b.title ?? b.text ?? '').trim())
    .filter((label) => label.length > 0)
  if (sections.length === 0) return null
  return (
    <ul className="menu menu-xs w-full shrink-0 gap-0.5 px-0">
      {sections.map((label, i) => (
        <li key={`${i}-${label}`}>
          <span className="flex items-center gap-2 truncate py-1" title={label}>
            <span className="badge badge-ghost badge-xs shrink-0 font-mono">{i + 1}</span>
            <span className="truncate">{label}</span>
          </span>
        </li>
      ))}
    </ul>
  )
}

/**
 * The persistent report panel beside the chat (IR-1). Renders the agent-authored report document
 * (`currentReport.html`) in a SANDBOXED iframe — same isolation the inline `RenderedReports` uses: the html is
 * a complete self-contained `<!doctype html>` document, so we isolate it via `srcDoc` with
 * `sandbox="allow-same-origin allow-modals"` (NO `allow-scripts` — the doc is static; `allow-same-origin` lets
 * us reach `contentWindow.print()` for Save-as-PDF, `allow-modals` opens the print dialog).
 *
 * The document is edited by the agent turn-by-turn via `applyReportEdits` + one server render (RenderReportEpic).
 * This panel is read-only: it shows the current render, a section outline, and a Print / Save-PDF action.
 */
export function ReportPanel({
  bare,
  printRef
}: {
  /**
   * Drop the panel's own header (title + print) so an enclosing card (W-2 WorkspacePanel) owns the single
   * header. The card hoists the Print action into its header via {@link printRef}. Default false → the
   * standalone panel keeps its own header.
   */
  bare?: boolean
  /**
   * In `bare` mode, the card populates this ref with the print/save-PDF handler so its header can host the
   * Print button (the panel no longer renders one). Ignored when not bare.
   */
  printRef?: React.MutableRefObject<(() => void) | null>
} = {}) {
  const report = useSelector((s) => s.chat.currentReport)
  const frameRef = useRef<HTMLIFrameElement | null>(null)

  const hasBlocks = report.blocks.length > 0
  const hasHtml = typeof report.html === 'string' && report.html.length > 0

  // Publish the print handler so the enclosing card header can host the Print button (bare mode).
  useEffect(() => {
    if (!printRef) return undefined
    printRef.current = () => frameRef.current?.contentWindow?.print()
    return () => {
      printRef.current = null
    }
  })

  if (!hasBlocks && !hasHtml) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center text-sm text-gray-400">
        <DocumentTextIcon className="h-8 w-8 opacity-50" />
        <span>Ask the agent to start a report.</span>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col gap-2 p-3">
      {!bare && (
        <div className="flex shrink-0 items-center justify-between gap-2">
          <span className="truncate text-sm font-semibold" title={report.title ?? 'Report'}>
            {report.title ?? 'Report'}
          </span>
          <span className="flex shrink-0 items-center gap-2">
            {report.status === 'rendering' && (
              <span className="loading loading-spinner loading-xs text-primary" aria-label="Rendering the report" />
            )}
            <button
              type="button"
              className="btn btn-ghost btn-xs gap-1"
              onClick={() => frameRef.current?.contentWindow?.print()}
              disabled={!hasHtml}
              title="Print or save this report as a PDF"
            >
              <PrinterIcon className="h-4 w-4" />
              Print / Save as PDF
            </button>
          </span>
        </div>
      )}

      {report.status === 'error' && (
        <div className="alert alert-warning shrink-0 py-2 text-xs">
          <span>Could not render the latest report changes. Showing the last successful version.</span>
        </div>
      )}

      <SectionOutline blocks={report.blocks} />

      {hasHtml ? (
        <iframe
          ref={frameRef}
          srcDoc={report.html ?? ''}
          sandbox="allow-same-origin allow-modals"
          title={report.title ?? 'Report'}
          className="block min-h-0 w-full grow rounded-lg border border-gray-200 bg-white"
        />
      ) : (
        <div className="flex min-h-0 grow items-center justify-center rounded-lg border border-gray-200 bg-base-100 text-sm text-gray-400">
          {report.status === 'rendering' ? 'Rendering…' : 'The report has no rendered content yet.'}
        </div>
      )}
    </div>
  )
}
